import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithErrorHandling } from "../baseQuery";

export const servicesApi = createApi({
  reducerPath: "servicesApi",
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ["Services"],
  refetchOnFocus: true,
  refetchOnReconnect: true,

  endpoints: (builder) => ({
    // Get All Services

    getServices: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        sortBy = "id",
        order = "asc",
      } = {}) => ({
        url: "/services",
        params: { page, limit, search, sortBy, order },
      }),

      /* Normalize API response */
      transformResponse: (response) => ({
        services: response?.data ?? [],
        total: response?.total ?? 0,
        page: response?.page ?? 1,
        limit: response?.limit ?? 10,
      }),

      /* Correct tag strategy */
      providesTags: (result) =>
        result?.services?.length
          ? [
              ...result.services.map(({ id }) => ({
                type: "Services",
                id: Number(id),
              })),
              { type: "Services", id: "LIST" },
            ]
          : [{ type: "Services", id: "LIST" }],

      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: true,
    }),

    // Get Service By ID

    getServiceById: builder.query({
      query: (id) => `/services/${id}`,
      transformResponse: (response) =>
        response?.data ? response.data : response,
      providesTags: (result, error, id) => [{ type: "Services", id }],
    }),

    // Get Service By Slug
    getServiceBySlug: builder.query({
      query: (slug) => `/services/slug/${slug}`,
      transformResponse: (response) =>
        response?.data ? response.data : response,
      providesTags: (result, error, slug) => [{ type: "Services", id: slug }],
    }),

    /* Create Service */
    createService: builder.mutation({
      query: (data) => ({
        url: "/services",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Services", id: "LIST" }],
    }),

    /* Update Service */
    updateService: builder.mutation({
      query: ({ id, data }) => ({
        url: `/services/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Services", id },
        { type: "Services", id: "LIST" },
      ],
    }),

    /* Delete Service */
    deleteService: builder.mutation({
      query: (id) => ({
        url: `/services/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Services", id: "LIST" }],
    }),

    /* Toggle Service Status */
    toggleServiceStatus: builder.mutation({
      query: (id) => ({
        url: `/services/${id}/toggle`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Services", id },
        { type: "Services", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetServicesQuery,
  useGetServiceByIdQuery,
  useGetServiceBySlugQuery,
  useCreateServiceMutation,
  useUpdateServiceMutation,
  useDeleteServiceMutation,
  useToggleServiceStatusMutation,
} = servicesApi;
export { baseQueryWithErrorHandling };
